
import { useState } from "react";
import { SlidersHorizontal, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from 'react-i18next';
import ServiceCategoryFilter from "./ServiceCategoryFilter";
import DomainFilter from "./DomainFilter";
import SubcategoryFilter from "./SubcategoryFilter";
import DistanceFilter from "./DistanceFilter";
import DateTimeFilter from "./DateTimeFilter";
import SessionTypeFilter from "./SessionTypeFilter";
import FreeSessionFilter from "./FreeSessionFilter";
import ThirdPartyRatingFilter from "./ThirdPartyRatingFilter";

interface MobileFiltersCollapsibleProps {
  filters: {
    serviceCategories: string[];
    effectiveDomain: string;
    serviceSubcategory: string;
    distance: string;
    date: Date | undefined;
    timeSlot: string;
    sessionType: string;
    showFreeSession: boolean;
    includeThirdPartyRating?: boolean;
  };
  setFilters: (filters: any) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  userLocation: { lat: number; lng: number } | null;
  userAddress: string | null;
  locationError: string | null;
  isGettingLocation: boolean;
  isLoadingAddress: boolean;
  requestLocationAccess: () => void;
  timeSlots: string[];
  isDateDisabled: (date: Date) => boolean;
  handleCategoryChange: (categoryName: string) => void;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  timezoneDisplay?: string | null;
  isApproximateLocation?: boolean;
}

const MobileFiltersCollapsible = ({
  filters,
  setFilters,
  searchQuery,
  setSearchQuery,
  userLocation,
  userAddress,
  locationError,
  isGettingLocation,
  isLoadingAddress,
  requestLocationAccess,
  timeSlots,
  isDateDisabled,
  handleCategoryChange,
  city,
  state,
  country,
  timezoneDisplay,
  isApproximateLocation
}: MobileFiltersCollapsibleProps) => {
  const { t } = useTranslation('practitioners');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <div className="mb-4">
      <Collapsible open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full flex items-center justify-between border-amber-200"
          >
            <span className="flex items-center">
              <SlidersHorizontal className="mr-2 h-4 w-4" /> 
              {t('filters')}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-medium flex items-center">
                  <SlidersHorizontal className="mr-2 h-5 w-5 text-amber-600" /> {t('filters')}
                </h2>
              </div>

              <div className="space-y-6">
                {/* Search Input */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('searchPractitioners')}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder=""
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <ServiceCategoryFilter 
                  selectedCategories={filters.serviceCategories}
                  onCategoryChange={handleCategoryChange}
                />

                <DomainFilter
                  selectedDomain={filters.effectiveDomain}
                  onDomainChange={(domain) => setFilters({...filters, effectiveDomain: domain})}
                />

                <SubcategoryFilter
                  selectedDomain={filters.effectiveDomain}
                  selectedSubcategory={filters.serviceSubcategory}
                  onSubcategoryChange={(subcategory) => setFilters({...filters, serviceSubcategory: subcategory})}
                />

                <DistanceFilter
                  distance={filters.distance}
                  onDistanceChange={(value) => setFilters({...filters, distance: value})}
                  userLocation={userLocation}
                  locationError={locationError}
                  city={city}
                  state={state}
                  country={country}
                  isGettingLocation={isGettingLocation}
                  isLoadingAddress={isLoadingAddress}
                  timezoneDisplay={timezoneDisplay}
                  isApproximateLocation={isApproximateLocation}
                  requestLocationAccess={requestLocationAccess}
                />

                <DateTimeFilter 
                  date={filters.date}
                  onDateChange={(date) => setFilters({...filters, date})}
                  timeSlot={filters.timeSlot}
                  onTimeSlotChange={(timeSlot) => setFilters({...filters, timeSlot})}
                  timeSlots={timeSlots}
                  isDateDisabled={isDateDisabled}
                />

                <SessionTypeFilter 
                  sessionType={filters.sessionType}
                  onSessionTypeChange={(sessionType) => setFilters({...filters, sessionType})}
                />

                <FreeSessionFilter
                  showFreeSession={filters.showFreeSession}
                  onToggle={(checked) => setFilters({...filters, showFreeSession: checked})}
                />

                <ThirdPartyRatingFilter
                  includeThirdPartyRating={filters.includeThirdPartyRating || true}
                  onToggle={(checked) => setFilters({...filters, includeThirdPartyRating: checked})}
                />
              </div>

              <div className="mt-8">
                <Button 
                  variant="outline" 
                  className="w-full border-amber-300 text-amber-700"
                  onClick={() => {
                    setFilters({
                      serviceCategories: ["all_types"],
                      effectiveDomain: "all_domains",
                      serviceSubcategory: "all_subcategories",
                      distance: "any_distance",
                      date: undefined,
                      timeSlot: "any_time",
                      sessionType: "all",
                      showFreeSession: false,
                      includeThirdPartyRating: true,
                    });
                    setSearchQuery("");
                  }}
                >
                  {t('clearAllFilters')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default MobileFiltersCollapsible;
